// Tonal ramp shared across all visualizations, anchored to Plexus green #0C9901.
export const GREENS = [
  'var(--viz-1)',
  'var(--viz-2)',
  'var(--viz-3)',
  'var(--viz-4)',
  'var(--viz-5)',
  'var(--viz-6)',
  'var(--viz-7)',
  'var(--viz-8)',
  'var(--viz-9)',
];

export function greenForCount(count: number, max: number): string {
  if (count <= 0) return 'transparent';
  if (max <= 0) return GREENS[0];
  const t = Math.min(1, count / max);
  const idx = Math.min(GREENS.length - 1, Math.floor(t * (GREENS.length - 1)) + 1);
  return GREENS[idx];
}

// Stable per-device green index based on device id hash.
export function greenForDevice(deviceId: string): string {
  let h = 0;
  for (let i = 0; i < deviceId.length; i++) h = ((h << 5) - h + deviceId.charCodeAt(i)) | 0;
  const idx = Math.abs(h) % (GREENS.length - 2) + 2; // skip 2 lightest
  return GREENS[idx];
}

export function hashTo(deviceId: string, salt = 0): number {
  let h = salt;
  for (let i = 0; i < deviceId.length; i++) h = ((h << 5) - h + deviceId.charCodeAt(i)) | 0;
  return Math.abs(h);
}

// Deterministic per-device color, taken from the reference streamgraph palette:
// deep saturated greens running from near-black forest to bright lime and a few
// pale sage tints. No washed-out greys — every band keeps its own saturation.
const GREEN_HUES = [88, 100, 112, 124, 136, 150];
// lightness / saturation pairs, dark and rich first, pale sage last
const TONES: Array<[number, number]> = [
  [12, 72],
  [18, 78],
  [24, 68],
  [30, 82],
  [36, 74],
  [42, 86],
  [48, 76],
  [55, 70],
  [63, 62],
  [72, 55],
];

// 60 deep green slots: hue rotates per tone so neighbouring slots differ in both
// hue and lightness.
const GREEN_RAMP: string[] = TONES.flatMap(([l, s], ti) =>
  GREEN_HUES.map((_, hi) => {
    const h = GREEN_HUES[(hi + ti * 2) % GREEN_HUES.length];
    return `hsl(${h} ${s}% ${l}%)`;
  })
);

// Interleave dark and light halves so consecutive slots contrast strongly.
export const DEVICE_RAMP: string[] = (() => {
  const half = Math.ceil(GREEN_RAMP.length / 2);
  const dark = GREEN_RAMP.slice(0, half);
  const light = GREEN_RAMP.slice(half).reverse();
  const out: string[] = [];
  for (let i = 0; i < half; i++) {
    if (dark[i]) out.push(dark[i]);
    if (light[i]) out.push(light[i]);
  }
  return out;
})();



export function colorForDevice(deviceId: string): string {
  return DEVICE_RAMP[hashTo(deviceId, 17) % DEVICE_RAMP.length];
}

// Assigns every device a *distinct* colour. Ids are sorted first so the result
// depends only on the set of devices — not on load order — and collisions are
// resolved by deterministically probing the next free slot.
export function buildDeviceColorMap(deviceIds: string[]): Map<string, string> {
  const map = new Map<string, string>();
  const used = new Set<number>();
  const unique = Array.from(new Set(deviceIds.filter(Boolean))).sort();
  const TOTAL = DEVICE_RAMP.length;

  unique.forEach((id) => {
    const start = hashTo(id, 17) % TOTAL;
    for (let step = 0; step < TOTAL; step++) {
      // Probe with a stride co-prime with TOTAL so fallbacks stay far apart.
      const slot = (start + step * 7) % TOTAL;
      if (used.has(slot)) continue;
      used.add(slot);
      map.set(id, DEVICE_RAMP[slot]);
      return;
    }
    map.set(id, colorForDevice(id));
  });

  return map;
}


